// ConcaveHull.ts
import * as THREE from 'three';
import Delaunator from 'delaunator';
import FVector from '@data/geometry/FVector';
import { calculateCentroid, sortPointsCCW } from '@rendering/DesignSchemes/DesignSchemeUtils';

/**
 * Compute alpha-shape concave hull for a set of 2D points.
 */
export function alphaShapeHull(points: FVector[], alpha = 1.5): FVector[] {
    const loops = alphaShapeHullLoops(points, alpha);
    if (loops.length === 0) return [];
    if (loops.length === 1) return loops[0];

    // Keep backward-compatible behavior: return one representative loop.
    let bestLoop = loops[0];
    let bestArea = Math.abs(signedArea2D(bestLoop));
    for (let i = 1; i < loops.length; i++) {
        const area = Math.abs(signedArea2D(loops[i]));
        if (area > bestArea) {
            bestArea = area;
            bestLoop = loops[i];
        }
    }
    return bestLoop;
}

/**
 * Compute alpha-shape concave hull loops for a set of 2D points.
 *
 * Unlike `alphaShapeHull`, this returns every disconnected boundary component.
 */
export function alphaShapeHullLoops(points: FVector[], alpha = 1.5): FVector[][] {
    if (points.length < 3) return [];

    const pts = points.map((p) => new THREE.Vector2(p.x, p.y));

    const delaunay = Delaunator.from(pts.map((p) => [p.x, p.y]));
    const tris = delaunay.triangles;

    const edgeMap = new Map<string, [number, number]>();

    for (let i = 0; i < tris.length; i += 3) {
        const a = tris[i],
            b = tris[i + 1],
            c = tris[i + 2];

        const pa = pts[a],
            pb = pts[b],
            pc = pts[c];
        const A = pa.distanceTo(pb);
        const B = pb.distanceTo(pc);
        const C = pc.distanceTo(pa);

        const s = (A + B + C) / 2;
        const areaExpr = s * (s - A) * (s - B) * (s - C);
        if (areaExpr <= 0) continue;

        const area = Math.sqrt(areaExpr);
        const circumR = (A * B * C) / (4 * area);

        if (!Number.isFinite(circumR) || circumR > alpha) continue;

        const edges: [number, number][] = [
            [a, b],
            [b, c],
            [c, a],
        ];

        for (const [i1, i2] of edges) {
            const key = `${Math.min(i1, i2)}-${Math.max(i1, i2)}`;
            if (edgeMap.has(key)) {
                edgeMap.delete(key); // internal edge (shared twice)
            } else {
                edgeMap.set(key, [i1, i2]);
            }
        }
    }

    if (edgeMap.size === 0) {
        const centroid = calculateCentroid(points);
        return [sortPointsCCW(points, centroid)];
    }

    // Build undirected adjacency from boundary edges.
    const neighbors = new Map<number, Set<number>>();
    const addNeighbor = (from: number, to: number) => {
        if (!neighbors.has(from)) neighbors.set(from, new Set<number>());
        neighbors.get(from)!.add(to);
    };

    for (const [i1, i2] of edgeMap.values()) {
        addNeighbor(i1, i2);
        addNeighbor(i2, i1);
    }

    const edgeKey = (a: number, b: number) => `${Math.min(a, b)}-${Math.max(a, b)}`;
    const visitedEdges = new Set<string>();
    const loops: number[][] = [];
    const starts = Array.from(neighbors.keys()).sort((i, j) => {
        const a = pts[i];
        const b = pts[j];
        if (a.x !== b.x) return a.x - b.x;
        return a.y - b.y;
    });

    // Traverse each boundary cycle by consuming unvisited edges.
    for (const start of starts) {
        const startNeighbors = Array.from(neighbors.get(start) ?? []);
        for (const first of startNeighbors) {
            const firstEdge = edgeKey(start, first);
            if (visitedEdges.has(firstEdge)) continue;

            const loop: number[] = [start];
            let prev = start;
            let curr = first;
            let closed = false;
            const maxSteps = edgeMap.size + 5;

            visitedEdges.add(firstEdge);
            loop.push(curr);

            for (let step = 0; step < maxSteps; step++) {
                const nbs = Array.from(neighbors.get(curr) ?? []);
                const next = nbs.find((n) => n !== prev);
                if (next === undefined) break;

                const k = edgeKey(curr, next);
                if (visitedEdges.has(k) && next !== start) break;

                visitedEdges.add(k);
                if (next === start) {
                    closed = true;
                    break;
                }

                loop.push(next);
                prev = curr;
                curr = next;
            }

            if (closed && loop.length >= 3) loops.push(loop);
        }
    }

    if (loops.length === 0) {
        const centroid = calculateCentroid(points);
        return [sortPointsCCW(points, centroid)];
    }

    return loops.map((idx) => idx.map((i) => points[i])).map((loop) => (signedArea2D(loop) < 0 ? loop.slice().reverse() : loop));
}

/**
 * Remove collinear points from a polygon.
 * Uses cross-product test (robust + orientation-free).
 */
export function simplifyCollinear(points: FVector[], eps = 1e-3): FVector[] {
    const n = points.length;
    if (n < 3) return points;

    const result: FVector[] = [];

    for (let i = 0; i < n; i++) {
        const prev = points[(i - 1 + n) % n];
        const curr = points[i];
        const next = points[(i + 1) % n];

        const ax = prev.x,
            ay = prev.y;
        const bx = curr.x,
            by = curr.y;
        const cx = next.x,
            cy = next.y;

        const cross = Math.abs((bx - ax) * (cy - ay) - (by - ay) * (cx - ax));

        if (cross < eps) continue; // skip collinear
        result.push(curr);
    }

    return result;
}

function signedArea2D(points: FVector[]): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        area += a.x * b.y - b.x * a.y;
    }
    return area * 0.5;
}
